import React from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  SafeAreaView,
  Alert,
  Platform,
  Image,
} from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { auth } from '@/utils/firebaseConfig';
import { Crown, Star, Check } from 'lucide-react-native';

export default function AssinaturaScreen() {
  const { colors } = useTheme();
  const { user } = useAuth();

  const handleAssinar = async (priceId: string, mode: 'subscription' | 'payment') => {
    if (!user) {
      Alert.alert('Erro', 'Usuário não autenticado');
      return;
    }

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        Alert.alert('Erro', 'Não foi possível validar sua sessão. Faça login novamente.');
        return;
      }

      const response = await fetch('/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ priceId: priceId, mode: mode }),
      });

      if (!response.ok) {
        Alert.alert('Erro', `Erro do servidor: ${response.status}`);
        return;
      }

      const data = await response.json();

      const { url } = data;
      if (url) {
        // Detectar se estamos em um ambiente mobile/PWA
        const isMobileOrPWA = Platform.OS === 'ios' || Platform.OS === 'android' ||
          (Platform.OS === 'web' && typeof window !== 'undefined' &&
            (window.navigator.userAgent.includes('Mobile') ||
              window.matchMedia('(display-mode: standalone)').matches));

        if (isMobileOrPWA && Platform.OS === 'web') {
          // Para PWA/Mobile Web: usar redirecionamento direto
          try {
            window.location.href = url;
          } catch (redirectError) {
            Alert.alert('Erro', 'Não foi possível abrir a página de pagamento. Tente novamente.');
          }
        } else {
          // Para apps nativos ou desktop: usar WebBrowser com depuração específica
          try {
            const result = await WebBrowser.openBrowserAsync(url, {
              // Configurações específicas para melhor compatibilidade
              presentationStyle: WebBrowser.WebBrowserPresentationStyle.FULL_SCREEN,
              controlsColor: '#000000',
              toolbarColor: '#FFFFFF'
            });

            if (result.type === 'cancel') {
              // Usuário cancelou o checkout
            } else if (result.type === 'dismiss') {
              // Checkout foi fechado
            }
          } catch (browserError: any) {
            // Fallback: tentar redirecionamento direto se WebBrowser falhar
            try {
              if (Platform.OS === 'web' && typeof window !== 'undefined') {
                window.location.href = url;
              } else {
                Alert.alert(
                  'Erro ao abrir navegador',
                  `Erro: ${browserError?.message || 'Erro desconhecido'}. Tente novamente ou use um navegador diferente.`
                );
              }
            } catch (fallbackError) {
              console.error('Erro no fallback:', fallbackError);
              Alert.alert('Erro', 'Não foi possível abrir a página de pagamento. Tente novamente.');
            }
          }
        }
      } else {
        console.error('URL não recebida do servidor');
        Alert.alert('Erro', 'Não foi possível iniciar o pagamento. Tente novamente.');
      }
    } catch (error: any) {
      console.error('Erro geral ao criar sessão de checkout:', {
        message: error?.message,
        stack: error?.stack,
        name: error?.name
      });
      Alert.alert('Erro', `Ocorreu um erro: ${error?.message || 'Erro desconhecido'}`);
    }
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    content: {
      padding: 24,
      paddingBottom: 40,
    },
    header: {
      alignItems: 'center',
      marginBottom: 32,
      marginTop: 20,
    },
    headerIcon: {
      marginBottom: 24,
      alignItems: 'center',
      justifyContent: 'center',
    },
    title: {
      fontSize: 28,
      fontFamily: 'Inter-Bold',
      color: colors.text,
      textAlign: 'center',
      marginBottom: 12,
    },
    subtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 24,
      fontFamily: 'Inter-Regular',
      maxWidth: 300,
    },
    plansContainer: {
      gap: 20,
      alignItems: 'center',
    },
    planCard: {
      backgroundColor: colors.surface,
      borderRadius: 24,
      padding: 24,
      borderWidth: 1,
      borderColor: colors.border,
      width: '100%',
      maxWidth: 400,
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 4,
      },
      shadowOpacity: 0.1,
      shadowRadius: 12,
      elevation: 4,
    },
    planCardHighlight: {
      borderColor: colors.primary,
      borderWidth: 2,
    },
    planHeader: {
      flexDirection: 'column', // Mudei para coluna para garantir que o preço nunca sobreponha o título
      alignItems: 'flex-start',
      marginBottom: 16,
      gap: 12,
    },
    planTitleContainer: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      flexWrap: 'wrap', // Adicionado para evitar sobreposição em telas pequenas
      gap: 8,
    },
    planTitle: {
      fontSize: 20,
      fontFamily: 'Inter-Bold',
      color: colors.text,
      marginBottom: 4,
    },
    bestOfferBadge: {
      backgroundColor: colors.warning,
      paddingHorizontal: 10,
      paddingVertical: 4,
      borderRadius: 20,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      alignSelf: 'flex-start',
    },
    bestOfferText: {
      color: '#FFFFFF',
      fontSize: 10,
      fontFamily: 'Inter-Bold',
      textTransform: 'uppercase',
    },
    priceContainer: {
      alignItems: 'flex-start', // Mudado de flex-end para flex-start para melhor layout com wrap
      justifyContent: 'center',
      minWidth: 100,
    },
    price: {
      fontSize: 22, // Reduzido de 24 para 22
      fontFamily: 'Inter-Bold',
      color: colors.primary,
    },
    divider: {
      height: 1,
      backgroundColor: colors.border,
      marginBottom: 24,
      opacity: 0.5,
    },
    benefitsContainer: {
      gap: 16,
      marginBottom: 32,
    },
    benefitItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    checkIcon: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: colors.success,
      alignItems: 'center',
      justifyContent: 'center',
    },
    benefitText: {
      fontSize: 16,
      color: colors.text,
      fontFamily: 'Inter-Medium',
      flex: 1,
    },
    subscribeButton: {
      backgroundColor: colors.primary,
      paddingVertical: 18,
      borderRadius: 16,
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'center',
      gap: 8,
      shadowColor: colors.primary,
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.3,
      shadowRadius: 16,
      elevation: 10,
    },
    subscribeButtonText: {
      color: '#FFFFFF',
      fontSize: 18,
      fontFamily: 'Inter-Bold',
    },
    guaranteeText: {
      textAlign: 'center',
      marginTop: 16,
      fontSize: 12,
      color: colors.textSecondary,
      fontFamily: 'Inter-Regular',
    },
  });

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <View style={styles.headerIcon}>
            <Image source={require('@/assets/images/crown.png')} style={{ width: 64, height: 64, backgroundColor: 'transparent', borderRadius: 32 }} resizeMode="contain" />
          </View>
          <Text style={styles.title}>Plano Premium Anual</Text>
          <Text style={styles.subtitle}>
            Desbloqueie todo o potencial do Põe no Racha com benefícios Premium e assinatura anual.
          </Text>
        </View>

        <View style={styles.plansContainer}>
          {/* Plano Anual */}
          <View style={[styles.planCard, styles.planCardHighlight]}>
            <View style={styles.planHeader}>
              <View style={styles.planTitleContainer}>
                <View>
                  <Text style={styles.planTitle}>Premium Anual</Text>
                  <View style={styles.bestOfferBadge}>
                    <Star size={12} color="#FFFFFF" fill="#FFFFFF" />
                    <Text style={styles.bestOfferText}>Assinatura</Text>
                  </View>
                </View>
                <View style={styles.priceContainer}>
                  <Text style={styles.price}>R$ 9,90/ano</Text>
                </View>
              </View>
            </View>

            <View style={styles.divider} />

            <View style={styles.benefitsContainer}>
              <View style={styles.benefitItem}>
                <View style={styles.checkIcon}>
                  <Check size={16} color="#FFFFFF" />
                </View>
                <Text style={styles.benefitText}>Rachas ilimitados</Text>
              </View>
              <View style={styles.benefitItem}>
                <View style={styles.checkIcon}>
                  <Check size={16} color="#FFFFFF" />
                </View>
                <Text style={styles.benefitText}>Vínculo de Bolso (Casais/Famílias)</Text>
              </View>
              <View style={styles.benefitItem}>
                <View style={styles.checkIcon}>
                  <Check size={16} color="#FFFFFF" />
                </View>
                <Text style={styles.benefitText}>Zero anúncios</Text>
              </View>
              <View style={styles.benefitItem}>
                <View style={styles.checkIcon}>
                  <Check size={16} color="#FFFFFF" />
                </View>
                <Text style={styles.benefitText}>Suporte prioritário</Text>
              </View>
            </View>

            <TouchableOpacity
              style={styles.subscribeButton}
              onPress={() => handleAssinar('price_1SrFWHKTgiAVxKeXc3AvlGzr', 'subscription')}
              activeOpacity={0.9}
            >
              <Text style={styles.subscribeButtonText}>Assinar Agora</Text>
              <Crown size={20} color="#FFFFFF" />
            </TouchableOpacity>

            <Text style={styles.guaranteeText}>
              Acesso liberado após confirmação do pagamento
            </Text>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
